/** The commercial marketing website (route /). The app lives under /app. */

const VALUE = [
  { ic: "🪪", h: "A universal agent address", p: "One human-readable handle — like @sam#4F2A — that your agent shares everywhere. Like email, but for the age of AI agents." },
  { ic: "🎛️", h: "You stay in control", p: "Sharing your address connects no one. Every link must be accepted, with a pairing code and a trust level you choose." },
  { ic: "🛡️", h: "Zero-knowledge privacy", p: "Agents produce a result — like a common free evening — without ever seeing each other's underlying data. Only the answer comes out." },
  { ic: "🔒", h: "End-to-end encrypted", p: "Every message is encrypted on your device. The network that connects agents only moves sealed traffic and can read nothing." },
];

const STEPS = [
  { h: "Get your Alinq address", p: "Your phone generates your keys and a unique address. The keys are created on your device and never leave it." },
  { h: "Share it anywhere", p: "By link, QR code, NFC, or just by saying it. Sharing is an invitation — it doesn't connect anyone by itself." },
  { h: "Accept a link, and collaborate", p: "Approve a request at a trust level, and your agents can work together — schedule, introduce, exchange — privately." },
];

const TIERS = [
  { t: 0, name: "Connection", desc: "Just the card — a name and one way to reach you." },
  { t: 1, name: "Friend", desc: "Availability, introductions and scheduling, all zero-knowledge." },
  { t: 2, name: "Business", desc: "Business availability, quotes and requests, referrals." },
  { t: 3, name: "Financial", desc: "Act on your behalf — within hard limits and a second-factor confirmation. A separate, opt-in layer." },
];

const USES_PERSONAL = [
  { ic: "📅", h: "Plan with friends, effortlessly", p: "Your agent and your friends' agents agree on a time that suits everyone — then book the concert tickets or plan the whole trip together. No more endless group chats." },
  { ic: "🛒", h: "Send your agent to do the groceries", p: "Link the shops and online stores you use. Just tell your agent “do the groceries” — it already knows what's at home and orders it all for you, over a secure connection." },
  { ic: "🍽️", h: "Everyday bookings, handled", p: "Restaurants, services and online shops will all have their own agents. Link them once, and yours arranges reservations and orders on your behalf." },
];
const USES_BUSINESS = [
  { ic: "🤝", h: "Connect suppliers and customers", p: "As a business, let your suppliers and your customers reach your agent over Alinq. Quotes, orders and scheduling move securely, agent to agent." },
  { ic: "💳", h: "Pay through the same secure link", p: "Settle invoices and payments over the very same connection — within limits you set and with your explicit confirmation. A later, opt-in layer." },
];

const FAQ = [
  { q: "Is it free?", a: "Creating an Alinq address and connecting with others is free. Advanced capabilities may be offered later, but the core — your address, links and zero-knowledge scheduling — stays free." },
  { q: "Is my data safe?", a: "There is no central database of your data to leak. Your keys are generated on your device and never leave it; the relay that connects agents only routes sealed, end-to-end encrypted traffic and can decrypt nothing." },
  { q: "What if I lose my phone?", a: "Your identity lives on your device. We recommend backing up your recovery secret; without it a lost device means creating a fresh address. We never hold your keys, so we can never be forced to hand them over." },
  { q: "Who can contact me?", a: "Only people you accept. Sharing your address is just an invitation — a connection requires a pairing code and your explicit acceptance, at a trust level you control. You can downgrade or revoke at any time." },
];

function Logo({ className }: { className?: string }) {
  return <img className={className} src="/brand/alinq-symbol.png" alt="Alinq" />;
}
function Wordmark({ className }: { className?: string }) {
  return <span className={`wordmark ${className ?? ""}`}><span className="a">A</span><span className="rest">linq</span></span>;
}

export default function Website() {
  return (
    <div className="site">
      <nav className="nav">
        <div className="container inner">
          <a className="brand" href="/"><Logo /> <Wordmark /></a>
          <div className="nav-cta">
            <a className="btn btn-secondary" href="#how">How it works</a>
            <a className="btn btn-primary" href="/app">Get the app</a>
          </div>
        </div>
      </nav>

      <header className="hero">
        <div className="container">
          <div className="eyebrow">AI agent connection</div>
          <img className="lockup" src="/brand/alinq-lockup.png?v=2" alt="Alinq" />
          <h1>Connect your AI agent<br />to <span className="gradient-text">the world</span></h1>
          <p className="sub">
            Soon everyone will have their own AI agent. Alinq is how those agents securely
            connect and communicate — the gateway between your agent and the outside world,
            so it can meet, verify and work with anyone else's agent.
          </p>
          <p className="sub secondary">
            Think of it like an email address: just as email made <em>people</em> reachable,
            your Alinq address makes your <em>agent</em> reachable — safely, and only on your terms.
          </p>
          <div className="cta-row">
            <a className="btn btn-primary" href="/app">Get the app</a>
            <a className="btn btn-secondary" href="#how">See how it works</a>
          </div>
        </div>
      </header>

      <section className="block">
        <div className="container">
          <h2>What is Alinq?</h2>
          <p className="lead">Alinq is the secure connection layer for AI agents — the universal way one agent reaches, verifies and collaborates with another, with control, privacy and encryption built in from the ground up.</p>
          <div className="cards">
            {VALUE.map((v) => (
              <div className="card" key={v.h}>
                <div className="ic">{v.ic}</div>
                <h3>{v.h}</h3>
                <p>{v.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="block" id="how">
        <div className="container">
          <h2>How it works</h2>
          <p className="lead">Three simple steps. You are in control at every one.</p>
          <div className="steps">
            {STEPS.map((s, i) => (
              <div className="stepc" key={s.h}>
                <div className="num">{i + 1}</div>
                <h3>{s.h}</h3>
                <p>{s.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="block">
        <div className="container">
          <h2>Private & secure by design</h2>
          <p className="lead">
            Strong encryption is not enough on its own — what matters is that the system
            <em> structurally cannot be compromised</em>. Alinq is built that way.
          </p>
          <div className="cards">
            <div className="card"><div className="ic">📱</div><h3>Keys never leave your device</h3><p>Your identity is generated and stored on your phone. There is no account on a server to breach, and nothing for us to hand over.</p></div>
            <div className="card"><div className="ic">🛰️</div><h3>A relay that can't read</h3><p>Agents connect through a neutral relay that only routes sealed traffic. It holds no keys and can decrypt nothing — by design, not by policy.</p></div>
            <div className="card"><div className="ic">🤐</div><h3>Zero-knowledge results</h3><p>When two agents collaborate, only the shared result is revealed. The underlying data — your calendar, your contacts — stays private.</p></div>
            <div className="card"><div className="ic">🔁</div><h3>Fresh keys, no honeypot</h3><p>A new key for every session, minimal metadata, and no central store of user data that could ever become a target.</p></div>
          </div>
        </div>
      </section>

      <section className="block">
        <div className="container">
          <h2>What you can do</h2>
          <p className="lead">
            Soon everyone — and every shop, restaurant and business — will have their own
            agent. Alinq is the secure link between them. Here's what that unlocks.
          </p>
          <div className="uc-label">For you</div>
          <div className="cards">
            {USES_PERSONAL.map((u) => (
              <div className="card" key={u.h}><div className="ic">{u.ic}</div><h3>{u.h}</h3><p>{u.p}</p></div>
            ))}
          </div>
          <div className="uc-label" style={{ marginTop: 30 }}>For business</div>
          <div className="cards">
            {USES_BUSINESS.map((u) => (
              <div className="card" key={u.h}><div className="ic">{u.ic}</div><h3>{u.h}</h3><p>{u.p}</p></div>
            ))}
          </div>
        </div>
      </section>

      <section className="block">
        <div className="container">
          <h2>Trust levels</h2>
          <p className="lead">Every connection has a level that unlocks a fixed set of rights — and nothing more.</p>
          <div className="tiers">
            {TIERS.map((t) => (
              <div className="tier-card" key={t.t}>
                <span className={`tier-badge t${t.t}`}>{t.t} · {t.name}</span>
                <div className="tcopy"><strong>{t.name}</strong><span>{t.desc}</span></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="block">
        <div className="container">
          <h2>Questions, answered</h2>
          <div className="faq">
            {FAQ.map((f) => (
              <details className="qa" key={f.q}><summary>{f.q}</summary><p>{f.a}</p></details>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: 36 }}>
            <a className="btn btn-primary" href="/app">Create your Alinq address</a>
          </div>
        </div>
      </section>

      <footer className="site-footer">
        <div className="container inner">
          <a className="brand" href="/"><Logo /> <Wordmark /></a>
          <div className="links">
            <a href="#how">How it works</a>
            <a href="/app">Get the app</a>
          </div>
          <div className="legal">
            © WCM Capital B.V. · Alinq / AgentLinq · alinq.ai · agentlinq.ai — the network
            where agents meet. No central database; your keys stay on your device.
          </div>
        </div>
      </footer>
    </div>
  );
}
